import {
  Path,
  Element,
  Transforms,
  Editor,
  Node,
  Range,
  Text,
  PointRef,
} from 'domains/reporter/RichTextEditor/core';
import {
  firstLetterOfFirstWord,
  shouldUncapitalizeNextWord,
} from '../../stitching/normalizationHelpers';
import type { CreateEnhanceEditorState } from '../../types';
import type { HeadingPluginElement, HeadingPluginPropertyOptions } from './types';
import { reportHasDuplicateHeadings } from '../headingError/utils';
import { PARAGRAPH_PLUGIN_ID } from '../paragraph';
import { HeadingLevel } from './constants';
import {
  normalizeText,
  getHeadingKeywordIndex,
  toTitleCase,
  upgradeHeadingKeywordToHeading,
  isCursorAfterFinalHeadingColon,
  maybeConvertTextParagraphsToHeadings,
  isHeadingNode,
  collapseTrailingColons,
} from './utils/normalization';
import {
  createInlineParagraph,
  isInlineParagraph,
  getOrInsertNextInlineParagraph,
} from '../paragraph/utils';
import { HEADING_PLUGIN_ID } from './types';
import { partialEditor } from '../../utils';
import { logger } from 'modules/logger';
import clone from 'ramda/es/clone';
import type { SlateContent } from '../../../Reporter/types';
import type { Ancestor, NodeEntry, TextUnit } from 'slate';
import { PICKLIST_PLUGIN_ID } from '../picklist';
import { LIST_PLUGIN_ID } from '../list';
import { BRACKET_TYPES } from '../../constants';
import { maybeDeleteAcrossHeadingBoundary } from './utils';
import { isRequiredFieldInRange } from '../../utils/requiredFields';

/*
 * We want to replace the text of the heading with the transformed text, but we also want to preserve the selection.
 *
 * We can just replace the text of each individual text node using Transforms.insertText.
 *
 * Finally, we select the original selection, so that the selection is preserved instead of being moved to the end of the heading.
 */
const replaceHeadingText = (
  editor: Editor,
  fn: (s: string) => string,
  path: Path,
  selection: Range
) => {
  for (const [textNode, textPath] of Editor.nodes(editor, {
    at: path,
    match: (n) => Text.isText(n),
  })) {
    const newText = fn(textNode.text);

    const nodeRange = {
      anchor: { path: textPath, offset: 0 },
      focus: { path: textPath, offset: textNode.text.length },
    };
    Transforms.insertText(editor, newText, { at: nodeRange });
  }
  Transforms.select(editor, selection);
};

/*
 * Unlike replaceHeadingText, this can't operate per-leaf: collapsing trailing colons often needs
 * to see across a leaf boundary (e.g. a freshly-typed "word:" leaf sitting right next to an old,
 * untouched ":" leaf) — each leaf looks "already collapsed" on its own, so a per-leaf transform
 * never converges and the normalizer loops forever. Instead we replace the whole node's text in
 * one shot, then restore the selection's relative offset if it was inside this heading (otherwise
 * it's untouched and already correct, since nothing else in the document moved).
 */
const collapseHeadingTrailingColons = (editor: Editor, path: Path, selection: Range): void => {
  const headingStart = Editor.start(editor, path);
  const headingEnd = Editor.end(editor, path);
  const currentText = Editor.string(editor, { anchor: headingStart, focus: headingEnd });
  const newText = collapseTrailingColons(currentText);
  if (newText === currentText) return;

  const isSelectionInThisHeading = Range.includes(
    { anchor: headingStart, focus: headingEnd },
    selection
  );
  const relativeOffset = isSelectionInThisHeading
    ? Editor.string(editor, { anchor: headingStart, focus: Range.start(selection) }).length
    : null;

  Transforms.delete(editor, { at: { anchor: headingStart, focus: headingEnd } });
  Transforms.insertText(editor, newText, { at: Editor.start(editor, path) });

  if (relativeOffset != null) {
    Transforms.select(editor, {
      path: [...path, 0],
      offset: Math.min(relativeOffset, newText.length),
    });
  }
};

const isMissingInlineParagraph = (fragmentNode: Node) => {
  return fragmentNode.type === HEADING_PLUGIN_ID;
};

export const enhanceEditorStateHeading: CreateEnhanceEditorState<HeadingPluginPropertyOptions> =
  ({ pluginID, headingKeywords, isInsertingMacro }) =>
  (editor: Editor) => {
    const {
      normalizeNode,
      deleteFragment,
      insertText,
      deleteBackward,
      deleteForward,
      insertFragment,
    } = editor;

    // Inserts `text` into the inline paragraph that follows the heading at `selection` (creating
    // one if needed), matching the spacing conventions used when continuing to type/dictate right
    // after a heading's colon.
    const insertTextAfterHeadingBoundary = (selection: Range, text: string): void => {
      const nextInlineParagraphEntry = getOrInsertNextInlineParagraph(editor, selection);
      if (nextInlineParagraphEntry == null) {
        logger.warn(
          '[enhanceEditorStateHeading] Unable to find or insert next inline paragraph; inserting text in-place.'
        );
        insertText(text);
        return;
      }
      const [nextInlineParagraphNode, nextInlineParagraphPath] = nextInlineParagraphEntry;
      const startPosition = Editor.start(editor, nextInlineParagraphPath);

      Transforms.select(editor, Editor.start(editor, startPosition));

      const nodeText = Node.string(nextInlineParagraphNode);
      const hasNoPrecedingSpace = !nodeText.startsWith(' ') && !text.startsWith(' ');

      // The pre-existing paragraph text may no longer start a sentence once `text` lands in front
      // of it (e.g. "Facet" landing before "Testing" should downcase it to "testing"). Decide this
      // — and grab a live ref to the letter's position — before any of the insertions below shift
      // it around; a forward-affinity point ref correctly tracks through each insertText call.
      let letterToLowercase: string | null = null;
      let uncapitalizeLetterRef: PointRef | null = null;
      if (nodeText.trim().length > 0) {
        const [letter, relativeIndex] = firstLetterOfFirstWord(nodeText);
        if (
          relativeIndex > -1 &&
          letter !== letter.toLowerCase() &&
          shouldUncapitalizeNextWord(text, nodeText, { nextWordFollowsProperNounRules: false })
        ) {
          letterToLowercase = letter;
          uncapitalizeLetterRef = Editor.pointRef(
            editor,
            { path: startPosition.path, offset: startPosition.offset + relativeIndex },
            { affinity: 'forward' }
          );
        }
      }

      // If neither the next inline paragraph nor the text to insert starts with a space, then we need to insert
      // a space
      if (hasNoPrecedingSpace) {
        Transforms.insertText(editor, ' ', {
          at: startPosition,
        });
        // If the text to insert doesn't start with a space but the inline paragraph does, then we move the selection
        // after the space
      } else if (nodeText.startsWith(' ') && !text.startsWith(' ')) {
        Transforms.move(editor, { distance: 1 });
      }

      Transforms.insertText(editor, text);

      // If the text in the inline paragraph is not empty and our text to insert
      // doesn't end with a space, then append a space so that the text doesn't merge
      if (nodeText.length > 0 && !nodeText.startsWith(' ') && !text.endsWith(' ')) {
        Transforms.insertText(editor, ' ');
      }

      const uncapitalizePoint = uncapitalizeLetterRef?.unref();
      if (uncapitalizePoint != null && letterToLowercase != null) {
        // Delete/insert both move the selection to just after themselves — restore it to where
        // the insertions above already correctly left it (right before this word) afterward.
        const selectionAfterInsertions = editor.selection;
        Transforms.delete(editor, { at: uncapitalizePoint, unit: 'character', distance: 1 });
        Transforms.insertText(editor, letterToLowercase.toLowerCase(), { at: uncapitalizePoint });
        if (selectionAfterInsertions != null) {
          Transforms.select(editor, selectionAfterInsertions);
        }
      }
    };

    editor.insertText = (text: string) => {
      let { selection: originalSelection } = editor;
      if (originalSelection == null) {
        insertText(text);
        return;
      }

      // Eagerly delete content in a custom manner if our selection spans a heading
      if (Range.isExpanded(originalSelection)) {
        const touchesRequiredField = isRequiredFieldInRange(editor, originalSelection);
        if (!touchesRequiredField) {
          maybeDeleteAcrossHeadingBoundary(editor, originalSelection, {
            selectAfterLeadingSpace: true,
          });
        }
        originalSelection = editor.selection;
      }

      const selectionStartPoint = Range.start(originalSelection);
      let textNodeEntry = Editor.node(editor, selectionStartPoint);
      let nodeEntry: NodeEntry<Ancestor> = Editor.parent(editor, textNodeEntry[1]);
      const [node, path] = nodeEntry;
      if (isInsertingMacro === true) {
        logger.info(
          '[enhanceEditorStateHeading] Inserting a macro, so we are not upgrading heading keywords'
        );
        insertText(text);
        Editor.normalize(editor, { force: true });
        return;
      }

      // Picklist values are literal content — even if they start with a heading keyword followed
      // by a colon, they must not be auto-upgraded to a heading.
      const isInsidePicklist = Editor.above(editor, {
        at: selectionStartPoint,
        match: (n) => Element.isElement(n) && n.type === PICKLIST_PLUGIN_ID,
      });
      if (isInsidePicklist) {
        logger.info(
          '[enhanceEditorStateHeading] Inserting a picklist, so we are not upgrading heading keywords'
        );
        insertText(text);
        return;
      }

      if (
        node.type === pluginID &&
        node.level === HeadingLevel.H1 &&
        reportHasDuplicateHeadings(editor, Node.string(node)) &&
        !text.includes(':')
      ) {
        insertText(text);
        Editor.normalize(editor, { force: true });
        return;
      }

      const isNodeInsideList = Editor.above(editor, {
        at: path,
        match: (n) => Element.isElement(n) && n.type === LIST_PLUGIN_ID,
      });

      if (
        text.includes(':') &&
        [PARAGRAPH_PLUGIN_ID, ...BRACKET_TYPES].some((id) => node.type === id) &&
        !isNodeInsideList &&
        headingKeywords != null
      ) {
        let headingKeyword = null;
        let textArrayIndex = -1;

        Editor.withoutNormalizing(editor, () => {
          insertText(text);
          // Track the point that sits at the tail end of the inserted text,
          // so that we can select this point if we upgrade to a heading.
          const afterInsertedTextPointRef = Editor.pointRef(
            editor,
            Editor.end(editor, editor.selection) ?? Editor.end(editor, []),
            { affinity: 'forward' }
          );

          // Refetch the node entry from the live selection after insertion.
          // Using the pre-insert selection can point to a stale path after split operations.
          const selectionAfterInsertPoint =
            editor.selection == null ? null : Range.start(editor.selection);
          if (
            selectionAfterInsertPoint == null ||
            !Editor.hasPath(editor, selectionAfterInsertPoint.path)
          ) {
            return;
          }

          textNodeEntry = Editor.node(editor, selectionAfterInsertPoint);
          nodeEntry = Editor.parent(editor, textNodeEntry[1]);
          [headingKeyword, textArrayIndex] = getHeadingKeywordIndex(
            editor,
            headingKeywords,
            nodeEntry
          );

          if (headingKeyword != null && textArrayIndex !== -1) {
            // Use only direct-child text (relative path depth 1) to avoid matching keywords
            // or colons from inside inline elements like picklists.
            const directChildText = Array.from(Node.texts(nodeEntry[0]))
              .filter(([, relPath]) => relPath.length === 1)
              .map(([textNode]) => textNode.text)
              .join('');
            const normalizedDirectChildText = directChildText.toLowerCase();
            const normalizedKeyword = headingKeyword.toLowerCase();

            const keywordIndexInNode = normalizedDirectChildText.indexOf(normalizedKeyword);
            const colonIndexInNode = directChildText.indexOf(':');

            // Only auto-upgrade if the first colon appears immediately after the keyword
            // (allowing only whitespace between keyword end and colon). This prevents
            // upgrading when a later sentence contains a colon.
            const isColonAfterKeyword =
              colonIndexInNode !== -1 &&
              keywordIndexInNode !== -1 &&
              keywordIndexInNode <= colonIndexInNode &&
              directChildText
                .slice(keywordIndexInNode + headingKeyword.length, colonIndexInNode)
                .trim() === '';

            if (isColonAfterKeyword) {
              upgradeHeadingKeywordToHeading(
                editor,
                nodeEntry,
                headingKeyword,
                textArrayIndex,
                Range.includes(
                  {
                    anchor: Editor.start(editor, nodeEntry[1]),
                    focus: Editor.end(editor, nodeEntry[1]),
                  },
                  selectionStartPoint
                ),
                afterInsertedTextPointRef
              );
            }
          }
        });
        return;
      }

      // If the target is already a heading and the incoming text itself contains a colon (e.g. a
      // whole dictated phrase like "testing: testing" arriving as one insertText call, rather
      // than word-by-word), the portion up to the colon completes the heading and everything
      // after it belongs in the following inline paragraph — regardless of cursor position,
      // since we already know this is heading content.
      if (node.type === pluginID && text.includes(':') && !isNodeInsideList) {
        const colonIndex = text.indexOf(':');
        const beforeColonText = text.slice(0, colonIndex + 1);
        const afterColonText = text.slice(colonIndex + 1);

        Editor.withoutNormalizing(editor, () => {
          insertText(beforeColonText);

          if (afterColonText !== '' && editor.selection != null) {
            insertTextAfterHeadingBoundary(editor.selection, afterColonText);
          }
        });
        Editor.normalize(editor, { force: true });
        return;
      }

      // If the cursor is currently in a heading but after the colon, we want to insert the text into
      // the adjacent inline paragraph instead
      if (
        Range.isCollapsed(originalSelection) &&
        isCursorAfterFinalHeadingColon(editor, originalSelection)
      ) {
        insertTextAfterHeadingBoundary(originalSelection, text);
        return;
      }

      insertText(text);
    };

    editor.deleteBackward = (unit: TextUnit) => {
      const { selection } = editor;
      if (!selection) return;

      const headingEntry = Editor.node(editor, selection, {
        depth: 1,
      });

      if (
        headingEntry != null &&
        Element.isElement(headingEntry[0]) &&
        headingEntry[0].type === pluginID
      ) {
        const [headingNode, headingPath] = headingEntry;
        if (Range.isCollapsed(selection) && Editor.isStart(editor, selection.anchor, headingPath)) {
          const previousNode = Editor.previous(editor, { at: headingPath });
          if (
            previousNode != null &&
            Element.isElement(previousNode[0]) &&
            previousNode[0].type === PARAGRAPH_PLUGIN_ID &&
            previousNode[0].shouldForceInline === true
          ) {
            Transforms.removeNodes(editor, { at: headingPath });
            const point = Editor.end(editor, previousNode[1]);
            Transforms.insertText(editor, Node.string(headingNode), {
              at: point,
            });
            Transforms.select(editor, point);
            Editor.normalize(editor, { force: true });
            return;
          }
        }

        if (
          Editor.isEnd(editor, Range.end(selection), headingPath) &&
          normalizeText(Node.string(headingNode)).length <= 1 &&
          unit === 'character'
        ) {
          Transforms.delete(editor, { at: headingPath });
          Editor.normalize(editor, { force: true });
          return;
        }

        // Normalize entire editor if we are deleting something in an H1, to make sure
        // we are removing possible heading errors
        if (headingEntry[0].level === HeadingLevel.H1) {
          deleteBackward(unit);
          Editor.normalize(editor, { force: true });
          return;
        }
      }

      deleteBackward(unit);
    };

    editor.deleteFragment = () => {
      const { selection } = editor;
      if (!selection) return;

      const headingEntries = Array.from(
        Editor.nodes<HeadingPluginElement>(editor, {
          at: selection,
          match: (n: Node): n is HeadingPluginElement =>
            n.type === pluginID && n.level === HeadingLevel.H1,
        })
      );

      // A crossing selection that also touches a required field must not take the
      // heading-boundary-split shortcut, since that path deletes content directly via
      // Transforms.delete and bypasses the required-field guard further down the plugin chain.
      const touchesRequiredField =
        Range.isExpanded(selection) && isRequiredFieldInRange(editor, selection);

      (!touchesRequiredField && maybeDeleteAcrossHeadingBoundary(editor, selection)) ||
        deleteFragment();

      // If any of the nodes in the selection is an H1, normalize the entire editor
      // to make sure that we remove heading errors
      if (headingEntries.length > 0) {
        Editor.normalize(editor, { force: true });
      }
    };

    // While we are no longer enforcing a paragraph between headings throughout the report, we are enforcing it
    // when a heading is first inserted into the report. This can happen via typing, dictating, or copy and pasting. In
    // particular, the latter two scenarios will trigger the logic in this function
    editor.insertFragment = (fragment: Array<Node>) => {
      const selection = editor.selection;
      if (selection == null) {
        const selectionErrorMessage =
          '[enhanceEditorStateHeading] Cannot insert fragment into editor as there is no selection.';
        logger.error(selectionErrorMessage, {
          editor: editor != null ? partialEditor(editor) : 'null',
          fragment: JSON.stringify(fragment),
        });
        return;
      }

      let fragmentToInsert: Array<Node> = clone(fragment);

      // If the fragment doesn't contain any headings, then we should check to see if we need to upgrade
      // any of these nodes to headings
      if (!fragment.some((node) => node.type === pluginID)) {
        if (headingKeywords != null) {
          fragmentToInsert = maybeConvertTextParagraphsToHeadings(
            editor,
            fragment,
            headingKeywords
          );

          if (!fragmentToInsert.some((node) => node.type === pluginID)) {
            insertFragment(fragmentToInsert);
            return;
          }

          logger.info(
            `[enhanceEditorStateHeading] Converted text paragraphs to headings when inserting fragment`,
            {
              fragment,
              fragmentToInsert,
              selection: editor?.selection ?? '',
            }
          );
        } else {
          insertFragment(fragmentToInsert);
          return;
        }
      }

      // Broadly, what we are going to do is ensure that each heading in the fragment is followed by
      // an inline paragraph, and that if the heading is preceded by either another heading or an inline paragraph, that
      // we add a paragraph between them.
      const formattedFragment: SlateContent = [];

      // If the current node is an inline paragraph, then we need to make sure to start our
      // fragment with an inline paragraph. This is because the inline paragraph will be
      // overridden by this fragment, and we want to make sure there is still an inline paragraph
      // at this point, as it means that the previous node to our selection is a heading.
      const currentNodeEntry = Editor.node(editor, selection, {
        depth: 1,
      });
      if (currentNodeEntry != null && isInlineParagraph(currentNodeEntry[0])) {
        formattedFragment.push(createInlineParagraph(''));
      }

      formattedFragment.push(fragmentToInsert[0]);

      // Follow a heading with an inline paragraph so it stays typeable. Skip when the next fragment node is
      // already an inline paragraph
      if (
        isMissingInlineParagraph(fragmentToInsert[0]) &&
        (fragmentToInsert.length === 1 || !isInlineParagraph(fragmentToInsert[1]))
      ) {
        formattedFragment.push(createInlineParagraph(''));
      }

      for (let i = 1; i < fragmentToInsert.length; i++) {
        formattedFragment.push(fragmentToInsert[i]);
        if (
          isMissingInlineParagraph(fragmentToInsert[i]) &&
          (i === fragmentToInsert.length - 1 || !isInlineParagraph(fragmentToInsert[i + 1]))
        ) {
          formattedFragment.push(createInlineParagraph(' '));
        }
      }

      insertFragment(formattedFragment);
    };

    editor.deleteForward = (unit: TextUnit) => {
      const { selection } = editor;
      if (!selection) return;
      const currentNodeEntry = Editor.node(editor, selection, {
        depth: 1,
      });

      if (
        currentNodeEntry != null &&
        Element.isElement(currentNodeEntry[0]) &&
        currentNodeEntry[0].type === pluginID
      ) {
        const [headingNode, headingPath] = currentNodeEntry;

        if (
          Editor.isStart(editor, Range.start(selection), headingPath) &&
          normalizeText(Node.string(headingNode)).length === 1 &&
          unit === 'character'
        ) {
          Transforms.delete(editor, { at: headingPath });
          return;
        }

        // Normalize entire editor if we are deleting something in an H1, to make sure
        // we are removing possible heading errors
        if (currentNodeEntry[0].level === HeadingLevel.H1) {
          deleteForward(unit);
          Editor.normalize(editor, { force: true });
          return;
        }
      } else if (
        currentNodeEntry != null &&
        Element.isElement(currentNodeEntry[0]) &&
        currentNodeEntry[0].type === PARAGRAPH_PLUGIN_ID &&
        currentNodeEntry[0].shouldForceInline === true &&
        Range.isCollapsed(selection) &&
        Editor.isEnd(editor, selection.anchor, currentNodeEntry[1])
      ) {
        const nextNode = Editor.next(editor, { at: currentNodeEntry[1] });
        if (nextNode != null && Element.isElement(nextNode[0]) && nextNode[0].type === pluginID) {
          const point = Editor.end(editor, currentNodeEntry[1]);
          Transforms.insertText(editor, Node.string(nextNode[0]), {
            at: point,
          });
          Transforms.removeNodes(editor, { at: nextNode[1] });

          Transforms.select(editor, point);
          Editor.normalize(editor, { force: true });
          return;
        }
      }

      deleteForward(unit);
    };

    editor.normalizeNode = (entry: NodeEntry) => {
      const [node, path] = entry;
      const { selection } = editor;

      if (Element.isElement(node) && node.type === pluginID && selection) {
        if (Node.string(node) !== collapseTrailingColons(Node.string(node))) {
          collapseHeadingTrailingColons(editor, path, selection);
          return;
        }

        if (
          node.level === HeadingLevel.H1 &&
          Node.string(node) !== Node.string(node).toUpperCase()
        ) {
          replaceHeadingText(editor, (s) => s.toUpperCase(), path, selection);
          return;
        }

        if (
          node.level === HeadingLevel.H2 &&
          Node.string(node) !== toTitleCase(Node.string(node))
        ) {
          replaceHeadingText(editor, toTitleCase, path, selection);
          return;
        }

        /**
         * If this heading's entire text is just colon(s)/whitespace (e.g. a lone ':' dictated or
         * stitched in right after an existing heading) and the heading immediately before it is
         * already complete (ends in ':'), this node is a redundant artifact rather than a heading
         * someone is legitimately starting — discard it and move the cursor into the following
         * inline paragraph instead of leaving the stray colon behind.
         */
        const rawHeadingText = Node.string(node);
        const isColonOnlyArtifact =
          rawHeadingText.includes(':') && rawHeadingText.replace(/[:\s]/g, '') === '';
        if (isColonOnlyArtifact) {
          const previousEntry = Editor.previous(editor, { at: path });
          const previousIsCompletedHeading =
            previousEntry != null &&
            isHeadingNode(previousEntry[0]) &&
            Node.string(previousEntry[0]).trim().endsWith(':');

          if (previousIsCompletedHeading) {
            // Only take over the selection if it's actually inside this node — otherwise it's
            // already positioned correctly elsewhere (e.g. right after dictated text that a
            // stitching-level redirect already placed in the inline paragraph), and forcing it to
            // the end of that paragraph would incorrectly jump past any of that paragraph's own
            // trailing content.
            const isSelectionInsideThisHeading = Range.includes(
              { anchor: Editor.start(editor, path), focus: Editor.end(editor, path) },
              selection
            );

            if (!isSelectionInsideThisHeading) {
              Transforms.removeNodes(editor, { at: path });
              return;
            }

            const nextInlineParagraphEntry = getOrInsertNextInlineParagraph(editor, {
              anchor: Editor.end(editor, path),
              focus: Editor.end(editor, path),
            });
            const nextInlineParagraphPathRef = nextInlineParagraphEntry
              ? Editor.pathRef(editor, nextInlineParagraphEntry[1])
              : null;

            Transforms.removeNodes(editor, { at: path });

            const nextInlineParagraphPath = nextInlineParagraphPathRef?.unref();
            if (nextInlineParagraphPath != null) {
              Transforms.select(editor, Editor.end(editor, nextInlineParagraphPath));
            }
            return;
          }
        }

        /**
         * If there is an empty heading but our cursor isn't currently in it,
         * delete it. This happens when something like a space is split off from
         * a previous heading. If a heading is empty because our cursor is in it, we don't
         * want to delete it since it means someone just began it.
         *
         * For example, with the following slate state:
         *  [
         *    {
         *      type: "heading",
         *      level: 1,
         *      children: [
         *        {text: "exam"}
         *      ]
         *    },
         *    {
         *      type: "heading",
         *      level: 1,
         *      children: [
         *        {text: ""}
         *      ]
         *    },
         *    {
         *      type: "heading",
         *      level: 1,
         *      children: [
         *        {text: ""}
         *      ]
         *    },
         *  ]
         *
         * And the cursor at [2,0]
         *
         * we WANT the end result to be:
         *
         *  [
         *    {
         *      type: "heading",
         *      level: 1,
         *      children: [
         *        {text: "exam"}
         *      ]
         *    },
         *    {
         *      type: "heading",
         *      level: 1,
         *      children: [
         *        {text: ""}
         *      ]
         *    },
         *  ]
         *
         * And cursor at [1,0]
         *
         * In the editor, it would look like:
         *
         *  EXAM
         *  |
         */
        if (
          normalizeText(Node.string(node)).length === 0 &&
          selection != null &&
          !Range.includes(
            { anchor: Editor.start(editor, path), focus: Editor.end(editor, path) },
            selection
          )
        ) {
          Transforms.removeNodes(editor, { at: path });
          return;
        }
      }

      normalizeNode(entry);
    };

    return editor;
  };
